/**
 * APIKEY provider catalog — specialty-media family (image / video / audio / embeddings / rerank / reader / coding tools).
 * Pure data; merged by apikey/index.ts via spread (god-file decomposition; semantic split).
 */
export const APIKEY_PROVIDERS_SPECIALTY = {
  nlpcloud: {
    id: "nlpcloud",
    alias: "nlpc",
    name: "NLP Cloud",
    icon: "psychology",
    color: "#2196F3",
    textIcon: "NLPC",
    website: "https://docs.nlpcloud.com",
    authHint:
      "Use your NLP Cloud API key in Authorization: Token <key>. OmniRoute targets the chatbot endpoint on https://api.nlpcloud.io/v1/gpu/<model>/chatbot by default.",
    apiHint:
      "NLP Cloud uses a proprietary chatbot API instead of OpenAI chat/completions. OmniRoute adapts OpenAI messages to input/context/history and exposes a local catalog of supported chatbot models.",
    hasFree: true,
    freeNote: "Trial credits for new accounts",
  },
  runwayml: {
    id: "runwayml",
    alias: "runway",
    name: "Runway",
    icon: "movie",
    color: "#111827",
    textIcon: "RW",
    website: "https://docs.dev.runwayml.com",
    authHint:
      "Use your Runway API key in Authorization: Bearer <key>. OmniRoute targets the current Runway API at https://api.dev.runwayml.com/v1 and sends the required X-Runway-Version header automatically.",
    apiHint:
      "Runway video generation is task-based. OmniRoute submits text-to-video or image-to-video jobs, polls /v1/tasks/{id}, and normalizes the finished video outputs back into the OpenAI-like /v1/videos/generations response.",
  },
  kie: {
    id: "kie",
    alias: "kie",
    name: "KIE.AI",
    icon: "hub",
    color: "#2563EB",
    textIcon: "KIE",
    website: "https://kie.ai",
  },
  pollinations: {
    id: "pollinations",
    alias: "pol",
    name: "Pollinations AI",
    icon: "local_florist",
    color: "#4CAF50",
    textIcon: "PO",
    website: "https://pollinations.ai",
    hasFree: true,
    anonymousFallback: true,
    freeNote:
      "Free keyless tier: openai, openai-fast, openai-large, qwen-coder, mistral, deepseek, grok, gemini-flash-lite-3.1, perplexity-fast, perplexity-reasoning. Premium models (claude, gemini, midijourney) require a Pollinations API key from enter.pollinations.ai.",
  },
  haiper: {
    id: "haiper",
    alias: "hp",
    name: "Haiper",
    icon: "videocam",
    color: "#6366F1",
    textIcon: "HP",
    website: "https://haiper.ai",
    authHint: "Get API key at haiper.ai/haiper-api",
  },
  leonardo: {
    id: "leonardo",
    alias: "leo",
    name: "Leonardo AI",
    icon: "palette",
    color: "#8B5CF6",
    textIcon: "LE",
    website: "https://leonardo.ai",
    authHint: "Get API key at leonardo.ai/developer",
  },
  ideogram: {
    id: "ideogram",
    alias: "ideo",
    name: "Ideogram",
    icon: "image",
    color: "#EC4899",
    textIcon: "ID",
    website: "https://ideogram.ai",
    authHint: "Get API key at ideogram.ai/docs/api",
  },
  suno: {
    id: "suno",
    alias: "suno",
    name: "Suno",
    icon: "music_note",
    color: "#F59E0B",
    textIcon: "SU",
    website: "https://suno.ai",
    authHint: "Paste session cookie from suno.ai (Clerk auth)",
  },
  udio: {
    id: "udio",
    alias: "udio",
    name: "Udio",
    icon: "music_note",
    color: "#10B981",
    textIcon: "UD",
    website: "https://udio.com",
    authHint: "Paste session cookie from udio.com (Supabase auth)",
  },
  "v0-vercel": {
    id: "v0-vercel",
    alias: "v0",
    name: "v0 (Vercel)",
    icon: "code_blocks",
    color: "#111827",
    textIcon: "V0",
    website: "https://v0.dev",
  },
  gitlab: {
    id: "gitlab",
    alias: "gitlab",
    name: "GitLab Duo PAT",
    icon: "hub",
    color: "#FC6D26",
    textIcon: "GL",
    website: "https://docs.gitlab.com/user/duo_agent_platform/code_suggestions/",
    authHint:
      "GitLab personal access token for the public Code Suggestions API. Configure a self-hosted base URL when not using gitlab.com.",
  },
  "voyage-ai": {
    id: "voyage-ai",
    alias: "voyage",
    name: "Voyage AI",
    icon: "blur_on",
    color: "#0F766E",
    textIcon: "VA",
    website: "https://www.voyageai.com",
    authHint: "Bearer API key for Voyage AI embeddings and rerank APIs.",
    hasFree: true,
    freeNote: "200M free tokens for embeddings and reranking",
  },
  "jina-ai": {
    id: "jina-ai",
    alias: "jina",
    name: "Jina AI",
    icon: "sort",
    color: "#2563EB",
    textIcon: "JA",
    website: "https://jina.ai",
    authHint: "Bearer API key for the Jina AI rerank API.",
    hasFree: true,
    freeNote: "10M free tokens on signup (non-commercial), no credit card required",
  },
  "fal-ai": {
    id: "fal-ai",
    alias: "fal",
    name: "Fal.ai",
    icon: "image",
    color: "#2563EB",
    textIcon: "FL",
    website: "https://fal.ai",
  },
  "stability-ai": {
    id: "stability-ai",
    alias: "stability",
    name: "Stability AI",
    icon: "image",
    color: "#8B5CF6",
    textIcon: "SA",
    website: "https://stability.ai",
  },
  "black-forest-labs": {
    id: "black-forest-labs",
    alias: "bfl",
    name: "Black Forest Labs",
    icon: "image",
    color: "#111827",
    textIcon: "BF",
    website: "https://blackforestlabs.ai",
  },
  recraft: {
    id: "recraft",
    alias: "recraft",
    name: "Recraft",
    icon: "image",
    color: "#EC4899",
    textIcon: "RC",
    website: "https://recraft.ai",
  },
  topaz: {
    id: "topaz",
    alias: "topaz",
    name: "Topaz",
    icon: "image",
    color: "#059669",
    textIcon: "TP",
    website: "https://topazlabs.com",
  },
  dify: {
    id: "dify",
    alias: "dify",
    name: "Dify",
    icon: "smart_toy",
    color: "#6366F1",
    textIcon: "DF",
    website: "https://dify.ai",
    hasFree: true,
    freeNote: "Free open-source AI app builder + RAG platform.",
    passthroughModels: true,
    authHint: "Get API key from your Dify instance.",
  },
  nomic: {
    id: "nomic",
    alias: "nomic",
    name: "Nomic",
    icon: "hub",
    color: "#7C3AED",
    textIcon: "NM",
    website: "https://nomic.ai",
    hasFree: true,
    freeNote: "Free Nomic Embed API. Open-source embeddings, no credit card required.",
    passthroughModels: true,
    authHint: "Get API key at atlas.nomic.ai",
  },
  firecrawl: {
    id: "firecrawl",
    alias: "fc",
    name: "Firecrawl",
    icon: "language",
    color: "#FB923C",
    textIcon: "FC",
    website: "https://firecrawl.dev",
    hasFree: true,
    notice: {
      text: "Free tier: 500 fetches/month, no credit card needed.",
      apiKeyUrl: "https://firecrawl.dev/app/api-keys",
    },
    serviceKinds: ["webFetch"],
  },
  "jina-reader": {
    id: "jina-reader",
    alias: "jr",
    name: "Jina Reader",
    icon: "menu_book",
    color: "#0EA5E9",
    textIcon: "JR",
    website: "https://jina.ai/reader",
    hasFree: true,
    notice: {
      text: "Free tier: 1M fetches/month.",
      apiKeyUrl: "https://jina.ai/api-dashboard",
    },
    serviceKinds: ["webFetch"],
  },
  tinyfish: {
    id: "tinyfish",
    alias: "tf",
    name: "TinyFish Fetch",
    icon: "language",
    color: "#0891B2",
    textIcon: "TF",
    website: "https://docs.tinyfish.ai/fetch-api",
    notice: {
      text: "Fetch does not use TinyFish credits. Submit up to 10 URLs per request (OmniRoute fetches one URL per call).",
      apiKeyUrl: "https://agent.tinyfish.ai/api-keys",
    },
    authHint: "X-API-Key from agent.tinyfish.ai/api-keys",
    serviceKinds: ["webFetch"],
  },
};
